import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useBlocker } from "@tanstack/react-router";
import { mrbSnBul } from "@oasis/domain";
import { ApiHata } from "@/lib/api";
import { dosyaYukle, YuklemeHatasi } from "@/lib/veri";
import { Band, Buton, Cip, Etiket, Modal, girdi, tabloBas, tabloHucre } from "@/components/parcalar";
import { cevir } from "@/lib/dil";
import { cn } from "@/lib/cn";
import {
  dosyalariEkle,
  etkinSn,
  excelsizDene,
  istekParcalari,
  satirGorunumu,
  secimeEkle,
  siradaki,
  snGir,
  sonucuIsle,
  tekrarDene,
  topluOzet,
  type Gonderim,
  type TopluSatir,
  type YukleYaniti,
} from "@/lib/mrb-toplu";

/** Kalıptaki {0}, {1} … yerine düğüm koyar: sayılar kalın yazılır, Türkçede kelime sırası değişebilir. */
function yerlestir(kalip: string, degerler: ReactNode[]): ReactNode {
  return kalip.split(/(\{\d+\})/).map((p, i) => {
    const m = /^\{(\d+)\}$/.exec(p);
    return <Fragment key={i}>{m ? degerler[Number(m[1])] : p}</Fragment>;
  });
}

const ACIKLAMA_RENGI = { "ink-2": "text-ink-2", "ink-3": "text-ink-3", bad: "text-bad" } as const;

/** Bir satırı gönderir; sonucu ham olarak döner (satıra çevirmek `sonucuIsle`'nin işi). */
async function gonder(s: TopluSatir): Promise<Gonderim> {
  const { pdf, alanlar, ekDosyalar } = istekParcalari(s);
  try {
    return { tur: "yanit", govde: await dosyaYukle<YukleYaniti>("/mrb/yukle", pdf, alanlar, ekDosyalar) };
  } catch (e) {
    if (e instanceof YuklemeHatasi) return { tur: "hata", status: e.status, govde: e.govde, mesaj: e.message };
    if (e instanceof ApiHata) return { tur: "hata", status: e.status, govde: null, mesaj: e.message };
    return { tur: "ag" };
  }
}

/**
 * D-060 "Upload MRBs": birden çok MRB PDF'i ve içerik excel'i seçilir ya da sürüklenir; aynı SN'li PDF ve excel tek satırda
 * eşlenir. Satırlar tek tek, sırayla `/api/mrb/yukle` ucuna (`toplu: "1"`) gider; biri hata verse de ötekiler devam eder.
 * Yükleme sürerken pencere kapanmaz ve uygulama içinde başka sayfaya gidilmez; "Stop after this file" bekleyenleri göndermez.
 * Satır listesi bu bileşenin durumunda durur: arkadaki liste yenilense de bozulmaz. Her tur bitince `onBitti` (liste yenilensin).
 */
export function MrbTopluYukleme({ onKapat, onBitti }: { onKapat: () => void; onBitti: () => void }) {
  const [satirlar, setSatirlar] = useState<TopluSatir[]>([]);
  // Döngü en güncel listeyi okusun diye durumun bir kopyası ref'te; ikisi hep birlikte yazılır.
  const liste = useRef<TopluSatir[]>([]);
  const yaz = useCallback((f: (l: TopluSatir[]) => TopluSatir[]) => {
    liste.current = f(liste.current);
    setSatirlar(liste.current);
  }, []);
  const [calisiyor, setCalisiyor] = useState(false);
  const calisiyorRef = useRef(false);
  /**
   * Döngünün göndereceği satırlar (`siradaki`): null = bütün bekleyenler ("Upload" turu); küme = yalnız bunlar. "Stop after this file"
   * ve tur sonu boş küme yapar; sonra satır düğmesi yalnız kendi satırını ekler.
   */
  const secim = useRef<ReadonlySet<number> | null>(null);
  /** Pencere kaldırıldı: döngü o anki dosyadan sonra durur. */
  const dur = useRef(false);
  const [durduruluyor, setDurduruluyor] = useState(false);
  /** İlk "Upload"tan sonra satırdaki "Retry" o satırı hemen gönderir; öncesinde yalnız sıraya koyar. */
  const [basladi, setBasladi] = useState(false);
  const [atlanan, setAtlanan] = useState<string[]>([]);
  const [snTaslak, setSnTaslak] = useState<Record<number, string>>({});
  const [surukleniyor, setSurukleniyor] = useState(false);
  const secici = useRef<HTMLInputElement>(null);

  // Bırakma alanının dışına düşen dosyayı tarayıcı açmasın: sayfa değişir, yükleme listesi kaybolur.
  useEffect(() => {
    const engelle = (e: DragEvent) => e.preventDefault();
    window.addEventListener("dragover", engelle);
    window.addEventListener("drop", engelle);
    return () => {
      window.removeEventListener("dragover", engelle);
      window.removeEventListener("drop", engelle);
    };
  }, []);

  // Yükleme sürerken uygulama içi gezinme (tarayıcının geri tuşu dahil) engellenir: pencere kalkarsa sonuçlar görünmez, döngü
  // durdurulamaz. Sekme kapatılır ya da sayfa yenilenirse tarayıcı sorar.
  const engelle = useCallback(() => calisiyorRef.current, []);
  useBlocker({ shouldBlockFn: engelle, enableBeforeUnload: engelle });

  // Pencere yine de kaldırılırsa (engeli aşan bir gezinme) döngü görünmeden sürmesin. StrictMode'un ikinci bağlaması bayrağı indirir.
  useEffect(() => {
    dur.current = false;
    return () => {
      dur.current = true;
    };
  }, []);

  const ekle = (dosyalar: File[]) => {
    if (!dosyalar.length) return;
    let atlananlar: string[] = [];
    yaz((l) => {
      const r = dosyalariEkle(l, dosyalar);
      atlananlar = r.atlanan;
      return r.satirlar;
    });
    setAtlanan(atlananlar);
  };

  const calistir = async () => {
    if (calisiyorRef.current) return;
    calisiyorRef.current = true;
    setCalisiyor(true);
    setBasladi(true);
    try {
      for (let s = siradaki(liste.current, secim.current); s && !dur.current; s = siradaki(liste.current, secim.current)) {
        const { id } = s;
        const sn = etkinSn(s);
        yaz((l) => l.map((x) => (x.id === id ? { ...x, durum: "yukleniyor" as const } : x)));
        const g = await gonder(s);
        yaz((l) => l.map((x) => (x.id === id ? { ...x, ...sonucuIsle(g, sn) } : x)));
      }
    } finally {
      calisiyorRef.current = false;
      // Tur bitti: bundan sonra satır düğmesi yalnız kendi satırını gönderir; öteki bekleyenler "Upload"u bekler.
      secim.current = new Set();
      setCalisiyor(false);
      setDurduruluyor(false);
      onBitti();
    }
  };

  /** "Upload": bütün bekleyenler gönderilir. */
  const yukle = () => {
    secim.current = null;
    void calistir();
  };

  /**
   * Satır düğmeleri: satırı yeniden sıraya koyar. Yükleme bir kez başladıysa yalnız o satır gönderilir; süren "Upload" turu onu
   * zaten alır, durdurulmuş ya da bitmiş turun bekleyenleri gönderilmez.
   */
  const yeniden = (id: number, f: (l: TopluSatir[]) => TopluSatir[]) => {
    yaz(f);
    if (!basladi) return;
    secim.current = secimeEkle(secim.current, id);
    void calistir();
  };

  const o = topluOzet(satirlar);
  const sayaclar: [string, number, "ok" | "neutral" | "warn" | "bad"][] = [
    [cevir("uploaded"), o.yuklendi, "ok"],
    [cevir("same file"), o.ayniDosya, "neutral"],
    [cevir("PDF not replaced"), o.pdfDegismedi, "warn"],
    [cevir("SN needed"), o.snGerekli, "warn"],
    [cevir("needs its PDF"), o.pdfGerekli, "warn"],
    [cevir("failed"), o.hata, "bad"],
  ];
  const suren = [o.yukleniyor ? `${cevir("uploading")} ${o.yukleniyor}` : null, o.bekliyor ? `${cevir("waiting")} ${o.bekliyor}` : null].filter(Boolean).join(" · ");

  return (
    <Modal baslik={cevir("Upload MRBs")} onKapat={onKapat} genis="cok" kilitli={calisiyor}>
      <p className="mb-3 text-sm text-ink-2">{cevir("Select or drop MRB PDFs, with or without their content excels (MRB_<SN>-input-EN.xlsx). A PDF and an excel with the same SN are paired on one row and sent together; a PDF without an excel joins the converter queue. From here an existing MRB's PDF is never replaced and an existing content excel is never changed. Rows are sent one by one; an error on one row does not stop the others.")}</p>
      <div
        className={cn("rounded-md border border-dashed border-line-strong bg-surface-2 px-4 py-6 text-center text-sm text-ink-2", surukleniyor && "border-orange")}
        onDragOver={(e) => {
          e.preventDefault();
          setSurukleniyor(true);
        }}
        onDragLeave={() => setSurukleniyor(false)}
        onDrop={(e) => {
          e.preventDefault();
          setSurukleniyor(false);
          ekle([...e.dataTransfer.files]);
        }}
      >
        {cevir("Drop PDF and excel files here, or")} <Buton kucuk onClick={() => secici.current?.click()}>{cevir("Select files")}</Buton>
        <div className="mt-1 text-xs text-ink-3">{cevir("PDF up to 200 MB each · content excel .xlsx")}</div>
        <input
          ref={secici}
          type="file"
          multiple
          accept=".pdf,.xlsx"
          className="hidden"
          onChange={(e) => {
            ekle([...(e.target.files ?? [])]);
            e.target.value = "";
          }}
        />
      </div>
      {atlanan.length > 0 && <Band tip="not" className="mt-3">{cevir("Not a PDF or .xlsx, skipped: {0}", atlanan.join(", "))}</Band>}

      {satirlar.length > 0 && (
        <>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink-2">
            <span>{yerlestir(cevir("{0} of {1} done"), [<b className="tnum">{o.biten}</b>, <b className="tnum">{o.toplam}</b>])}</span>
            {sayaclar.some(([, n]) => n > 0) && (
              <span className="inline-flex flex-wrap gap-1">
                {sayaclar.filter(([, n]) => n > 0).map(([ad, n, ton]) => <Etiket key={ad} tone={ton}>{ad} {n}</Etiket>)}
              </span>
            )}
            {suren && <span className="text-ink-3">{suren}</span>}
          </div>
          <div className="scroll-thin mt-2 overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-line">
                  <th className={tabloBas}>#</th>
                  <th className={tabloBas}>{cevir("Files")}</th>
                  <th className={tabloBas}>Assembly SN</th>
                  <th className={tabloBas}>{cevir("Result")}</th>
                </tr>
              </thead>
              <tbody>
                {satirlar.map((s, i) => {
                  const g = satirGorunumu(s);
                  const sn = etkinSn(s);
                  const taslak = snTaslak[s.id] ?? s.girilenSn ?? "";
                  return (
                    <tr key={s.id} className="border-b border-line last:border-0">
                      <td className={cn(tabloHucre, "tnum text-ink-3")}>{i + 1}</td>
                      <td className={tabloHucre}>
                        {s.pdf && <Cip>{s.pdf.name}</Cip>}
                        {s.excel && <Cip>{s.excel.name}</Cip>}
                      </td>
                      <td className={tabloHucre}>
                        {g.snGirisi ? (
                          <div className="flex flex-wrap items-center gap-1.5">
                            <input className={cn(girdi, "max-w-[130px]")} placeholder={cevir("e.g. 1310530-1")} value={taslak} onChange={(e) => setSnTaslak((t) => ({ ...t, [s.id]: e.target.value }))} />
                            <Buton kucuk disabled={!mrbSnBul(taslak)} onClick={() => yeniden(s.id, (l) => snGir(l, s.id, taslak))}>{cevir("Retry")}</Buton>
                          </div>
                        ) : sn ? (
                          <b className="font-mono">{sn}</b>
                        ) : (
                          <span className="text-ink-3">—</span>
                        )}
                      </td>
                      <td className={tabloHucre}>
                        {g.etiketler.map((e, j) => (
                          <Fragment key={j}>
                            {j > 0 && " "}
                            <Etiket tone={e.ton}>{e.metin}</Etiket>
                          </Fragment>
                        ))}
                        {g.aciklama && <div className={cn("mt-1 text-xs", ACIKLAMA_RENGI[g.aciklama.ton])}>{g.aciklama.metin}</div>}
                        {g.uyarilar.map((u, j) => <div key={j} className="mt-1 text-xs text-warn">{u}</div>)}
                        {g.dugme && (
                          <div className="mt-1 flex flex-wrap gap-1.5">
                            <Buton kucuk onClick={() => yeniden(s.id, (l) => (g.dugme === "excelsiz" ? excelsizDene(l, s.id) : tekrarDene(l, s.id)))}>{g.dugme === "excelsiz" ? cevir("Retry without excel") : cevir("Retry")}</Buton>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="mt-4 flex flex-wrap gap-2">
        {/* Upload yükleme sürerken yerinde kalır, kilitlenir; Stop onun yerine çizilmez: Upload'a çift tıkın ikinci tıkı Stop'a düşmesin. */}
        <Buton tur="birincil" disabled={calisiyor || !o.bekliyor} onClick={yukle}>{cevir("Upload")}</Buton>
        {calisiyor && <Buton disabled={durduruluyor} onClick={() => { secim.current = new Set(); setDurduruluyor(true); }}>{cevir("Stop after this file")}</Buton>}
        <Buton disabled={calisiyor} title={calisiyor ? cevir("Available when the current file finishes") : undefined} onClick={onKapat}>{cevir("Close")}</Buton>
      </div>
      <p className="mt-2 text-xs text-ink-3">{cevir("Closing the window during an upload is blocked; \"Stop after this file\" leaves the waiting files unsent. Rows that need you (SN, retry) stay on the list until the window is closed.")}</p>
    </Modal>
  );
}
